import { useState } from 'react';
import { X, Loader2, Gift, Truck, ShieldCheck, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

export function SignInModal() {
  const { showSignInModal, setShowSignInModal, signInWithGoogle, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [localLoading, setLocalLoading] = useState(false);

  const handleClose = () => {
    setShowSignInModal(false);
  };

  const handleGoogleSignIn = async () => {
    setLocalLoading(true);
    try {
      await signInWithGoogle();
      toast({ title: 'Welcome back!' });
      handleClose();
    } catch (error: any) {
      toast({
        title: 'Sign-in failed',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLocalLoading(false);
    }
  };

  const handlePhoneAuth = () => {
    handleClose();
    navigate('/phone-auth');
  };

  if (!showSignInModal) return null;

  const isLoading = authLoading || localLoading;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-foreground/60 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-card rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 hover:bg-secondary rounded-full transition-colors z-10"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 via-aurakart-yellow/10 to-aurakart-green/10 px-6 pt-8 pb-6">
            <h2 className="text-2xl font-bold">Welcome to AuraKart</h2>
            <p className="text-muted-foreground mt-1">
              Sign in to continue shopping
            </p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Primary: Google Sign-In */}
            <Button
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              variant="default"
              size="lg"
              className="w-full bg-black hover:bg-black/90 text-white font-medium"
            >
              {isLoading ? (
                <Loader2 className="mr-3 h-5 w-5 animate-spin" />
              ) : (
                <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              Continue with Google
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-3 text-muted-foreground">or</span>
              </div>
            </div>

            {/* Secondary: Phone Auth */}
            <Button
              onClick={handlePhoneAuth}
              disabled={isLoading}
              variant="outline"
              size="lg"
              className="w-full font-medium"
            >
              <Phone className="mr-3 h-5 w-5" />
              Continue with Phone
            </Button>

            {/* Benefits */}
            <div className="pt-6 border-t">
              <p className="text-center text-sm font-medium text-muted-foreground mb-4">
                Why sign in?
              </p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Gift className="h-6 w-6 text-primary" />
                  </div>
                  <span className="text-xs font-medium">Exclusive Offers</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-aurakart-green/10 flex items-center justify-center">
                    <Truck className="h-6 w-6 text-discount" />
                  </div>
                  <span className="text-xs font-medium">Track Orders</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-aurakart-blue/10 flex items-center justify-center">
                    <ShieldCheck className="h-6 w-6 text-aurakart-blue" />
                  </div>
                  <span className="text-xs font-medium">Secure Checkout</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <p className="text-xs text-muted-foreground text-center mt-6">
              By signing in, you agree to our{' '}
              <span className="text-primary underline">Terms</span> and{' '}
              <span className="text-primary underline">Privacy Policy</span>
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}